import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import {
  Timer,
  Users,
  ShieldCheck,
  Sparkles,
  Quote,
  Star,
  Zap,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
} from "lucide-react";
import { GlitchMonitor, TerminalModal } from "../../components/puzzle/GlitchPuzzle";
import { getUpcomingMilestones } from "../../data/roadmap";
import { Map } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [loadingT, setLoadingT] = useState(true);
  const [terminalOpen, setTerminalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await axios.get(`${API}/api/testimonials`, { params: { limit: 12 } });
        if (!cancelled) setTestimonials(res.data || []);
      } catch (e) {
        console.warn("Impossible de charger les témoignages", e);
        if (!cancelled) setTestimonials([]);
      } finally {
        if (!cancelled) setLoadingT(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const fallbackTestimonials = [
    {
      quote: "Un super moment d'équipe, même à distance.",
      firstName: "Laura",
      companyName: "Graphiste • Arobace",
      tag: "Team building",
      rating: 5,
    },
    {
      quote: "Simple à organiser pour moi, et les collègues étaient à fond.",
      firstName: "Sarah",
      companyName: "Production • Deux Croches Rondes",
      tag: "Team building",
      rating: 5,
    },
    {
      quote: "Ça a vraiment rapproché l'équipe en moins d'une heure.",
      firstName: "Michael",
      companyName: "Direction • Francopack",
      tag: "Team building",
      rating: 5,
    },
  ];

  const items = testimonials && testimonials.length > 0 ? testimonials : fallbackTestimonials;

  return (
    <div className="bg-slate-50 text-slate-900">

      {/* ── HERO ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-900">
        <div
          className="pointer-events-none absolute inset-0 opacity-80"
          style={{
            background:
              "radial-gradient(circle at 0% 0%, rgba(129,140,248,0.45) 0, transparent 55%), radial-gradient(circle at 90% 10%, rgba(236,72,153,0.22) 0, transparent 55%), radial-gradient(circle at 50% 100%, rgba(56,189,248,0.16) 0, transparent 60%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 70%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 70%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.11]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(to bottom, rgba(255,255,255,0.55) 0px, rgba(255,255,255,0.55) 1px, transparent 2px, transparent 7px)",
            maskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 72%)",
            WebkitMaskImage: "radial-gradient(circle at 50% 35%, black 0%, transparent 72%)",
          }}
        />
        <div
          className="pointer-events-none absolute -bottom-40 -right-40 h-[420px] w-[420px] rounded-full opacity-30 blur-3xl"
          style={{
            background: "radial-gradient(circle at 30% 30%, rgba(249,115,22,0.85) 0%, rgba(249,115,22,0.0) 60%)",
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 min-h-[88vh] flex items-center">
          <div className="w-full max-w-3xl mx-auto text-center">
            <div className="flex flex-wrap items-center justify-center gap-2 mb-6 mt-3">
              <span className="text-[11px] font-semibold tracking-[0.28em] text-indigo-200/90 uppercase">
                Team building digital
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2 py-1 text-[11px] font-medium text-indigo-100">
                <Sparkles className="h-3.5 w-3.5 text-orange-300" />
                Prêt en 5 minutes
              </span>
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2 py-1 text-[11px] font-medium text-indigo-100">
                <Zap className="h-3.5 w-3.5 text-yellow-400" />
                Aucune logistique
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold leading-tight text-white mb-5">
               Un escape game pour votre équipe,
              <br className="hidden sm:block" />
              <span className="text-indigo-300"> clé en main et sans déplacement</span>
              <span className="text-orange-300">.</span>
            </h1>

            <p className="text-sm sm:text-base md:text-lg text-indigo-100/90 max-w-2xl mx-auto leading-relaxed">
              Achetez un pack, partagez un code, lancez la partie. Vos collaborateurs jouent depuis leur navigateur, où qu'ils soient, en 5 minutes, sans aucune installation.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/catalogue"
                className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 rounded-full text-sm sm:text-base font-semibold text-white
                           bg-indigo-500 hover:bg-indigo-400
                           shadow-md shadow-indigo-500/30 hover:shadow-indigo-400/40
                           ring-1 ring-white/10 hover:ring-white/25
                           transition-all duration-200"
              >
                Voir les escape games
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/offres"
                className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 rounded-full text-sm sm:text-base font-semibold
                           text-indigo-100 border border-indigo-300/60 hover:border-indigo-200
                           hover:bg-indigo-900/40 ring-1 ring-white/10
                           transition-all duration-200"
              >
                Voir les tarifs
                <span className="ml-2 inline-block opacity-80 group-hover:opacity-100">✦</span>
              </Link>
            </div>

            {/* Métriques */}
            <div className="mt-5 text-[12px] sm:text-xs text-indigo-100/80 flex flex-col sm:flex-row items-center justify-center gap-2">
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-300/80" />
                60 à 90 min par session
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-orange-300/80" />
                4 à 6 joueurs par session
              </span>
              <span className="hidden sm:inline">•</span>
              <span className="inline-flex items-center gap-2">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-indigo-300/80" />
                Navigateur + votre outil visio habituel
              </span>
            </div>

            {/* Pré-requis */}
            <div className="mt-6 mx-auto max-w-2xl rounded-2xl border border-white/15 bg-white/5 px-4 py-3 text-left text-[12px] text-indigo-100/90 backdrop-blur">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 inline-flex h-7 w-7 items-center justify-center rounded-xl bg-white/10 border border-white/10">
                  <GlitchMonitor size="h-4 w-4" className="text-orange-300" onClick={() => setTerminalOpen(true)} />
                </span>
                <div className="leading-relaxed">
                  <p className="font-semibold text-white/95">Pré-requis</p>
                  <p className="text-indigo-100/85">
                    Un <span className="font-medium">navigateur sur ordinateur</span> et{" "}
                    <span className="font-medium">votre outil de visio habituel</span> (Teams / Meet / Zoom).{" "}
                    <span className="opacity-90">Aucun téléchargement.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Trust row */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 mb-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[12px] text-indigo-100/90 backdrop-blur">
                <ShieldCheck className="h-4 w-4 text-indigo-200" />
                Paiement &amp; factures via Stripe
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[12px] text-indigo-100/90 backdrop-blur">
                <Users className="h-4 w-4 text-orange-300" />
                Scénarios coopératifs, zéro friction
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[12px] text-indigo-100/90 backdrop-blur">
                <CalendarDays className="h-4 w-4 text-indigo-200" />
                Planifiable en quelques clics
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── POUR QUI ? ── */}
      <section className="bg-slate-100 py-16 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-[11px] uppercase tracking-[0.22em] text-slate-500 mb-2">Fait pour vous si…</p>
          <h2 className="text-center text-2xl font-semibold text-slate-900 mb-8">Vous vous reconnaissez dans l'une de ces situations ?</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              // { icon: <Laptop2 className="h-5 w-5" />, text: "Votre équipe travaille en remote ou en hybride et les moments informels manquent." },
              { icon: <GlitchMonitor size="h-5 w-5" onClick={() => setTerminalOpen(true)} />, text: "Votre équipe travaille en remote ou en hybride et les moments informels manquent." },

              { icon: <CalendarDays className="h-5 w-5" />, text: "Vous devez organiser un team building mais n'avez ni le temps ni le budget pour un prestataire sur mesure." },
              { icon: <Users className="h-5 w-5" />, text: "Vos collaborateurs sont dispersés géographiquement et se déplacer ensemble est compliqué." },
              { icon: <Zap className="h-5 w-5" />, text: "Vous voulez quelque chose de prêt rapidement, sans des semaines de préparation." },
              { icon: <Sparkles className="h-5 w-5" />, text: "Vous cherchez une activité qui crée de la vraie interaction, pas juste un afterwork Zoom." },
              { icon: <ShieldCheck className="h-5 w-5" />, text: "Votre budget événementiel est limité mais vous voulez un moment mémorable." },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <span className="shrink-0 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-900 text-orange-300">
                  {item.icon}
                </span>
                <p className="text-sm text-slate-700 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── AVANTAGES ── */}
      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-center text-slate-900">
            Tout le fun de l'escape room, sans la logistique
          </h2>
          <p className="mt-3 text-sm sm:text-base text-center text-slate-500 max-w-2xl mx-auto">
            Vous achetez, vous partagez un code, ils jouent. C'est tout.
          </p>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {[
              {
                // icon: <Laptop2 className="h-6 w-6" />,
                icon: <GlitchMonitor size="h-6 w-6" onClick={() => setTerminalOpen(true)} />,
                accent: "from-indigo-500/20 to-sky-400/10",
                ring: "ring-indigo-100",
                title: "Zéro installation, zéro friction",
                text: "Navigateur sur ordinateur + votre outil visio habituel (Teams, Meet, Zoom). Les joueurs rejoignent avec un code. Aucun téléchargement, aucune création de compte pour les joueurs.",
              },
              {
                icon: <Timer className="h-6 w-6" />,
                accent: "from-orange-500/20 to-pink-400/10",
                ring: "ring-orange-100",
                title: "Lancé en 5 minutes",
                text: "Achetez un pack, récupérez vos codes session dans votre espace, transmettez-les à vos équipes. Pas de coordination avec un prestataire, pas d'attente.",
              },
              {
                icon: <Users className="h-6 w-6" />,
                accent: "from-indigo-500/20 to-violet-400/10",
                ring: "ring-indigo-100",
                title: "Une vraie expérience collective",
                text: "Des scénarios coopératifs conçus pour forcer la communication et la collaboration. Chaque joueur a un rôle, personne n'est spectateur.",
              },
            ].map((a) => (
              <div
                key={a.title}
                className="group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition hover:shadow-md"
              >
                <div
                  className={`pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-br ${a.accent} opacity-0 group-hover:opacity-100 transition-opacity`}
                />
                <div className="relative flex items-start gap-4">
                  <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-900 text-white ring-4 ${a.ring} shadow-sm`}>
                    <span className="text-orange-300 p-1">{a.icon}</span>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{a.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{a.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMMENT ÇA MARCHE ── */}
      <section className="bg-slate-50 py-16 border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-center text-slate-900 mb-3">
            En pratique, comment ça marche ?
          </h2>
          <p className="text-sm text-center text-slate-500 mb-10 max-w-xl mx-auto">
            Côté organisateur, c'est trois étapes. Côté joueurs, c'est un code et c'est parti.
          </p>

          <div className="grid gap-4 sm:grid-cols-3">
            {[
              {
                step: "1",
                title: "Achetez un pack",
                text: "Choisissez un jeu, achetez une ou plusieurs sessions depuis la page produit. Paiement sécurisé via Stripe, facture automatique.",
              },
              {
                step: "2",
                title: "Partagez le code",
                text: "Vos codes session apparaissent dans votre espace. Transmettez-les à vos équipes avec la date et l'heure. Notre générateur de mails vous facilite la tâche.",
              },
              {
                step: "3",
                title: "Ils jouent",
                text: "Les joueurs entrent le code sur la page Jouer. En quelques minutes, tout le monde est dans le lobby et la partie peut démarrer.",
              },
            ].map((s) => (
              <div key={s.step} className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-bold shrink-0">
                    {s.step}
                  </span>
                  <h3 className="text-sm font-semibold text-slate-900">{s.title}</h3>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/guide"
              className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 transition"
            >
              Voir le guide complet
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── TÉMOIGNAGES ── */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-3">
            Ce qu'en disent les équipes
          </h2>
          <p className="text-sm text-slate-500 mb-10">
            Des retours terrain : organisation, engagement, cohésion.
          </p>

          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={22}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 4500, disableOnInteraction: false }}
            loop={true}
            className="pb-12"
          >
            {items.map((t, idx) => (
              <SwiperSlide key={idx}>
                <div className="h-full rounded-2xl p-[1px] bg-gradient-to-br from-indigo-200 via-slate-200 to-orange-200">
                  <div className="h-full p-6 bg-white rounded-2xl shadow-sm border border-slate-100 text-left transition hover:shadow-md">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center rounded-full bg-indigo-900 text-white px-2.5 py-1 text-[11px] font-semibold">
                        {t.tag}
                      </span>
                      <div className="flex items-center gap-0.5 text-orange-500">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-current" />
                        ))}
                      </div>
                    </div>
                    <div className="mt-5 flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl bg-orange-50 p-2 ring-1 ring-orange-100">
                        <Quote className="h-5 w-5 text-orange-500" />
                      </div>
                      <p className="text-slate-700 leading-relaxed">"{t.quote}"</p>
                    </div>
                    <div className="mt-6 pt-4 border-t border-slate-100">
                      <p className="font-semibold text-slate-900">{t.firstName}</p>
                      <p className="text-sm text-slate-500">{t.companyName}</p>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* ── ROADMAP TEASER ── */}
      <section className="bg-slate-50 py-16 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.28em] text-indigo-600 uppercase mb-3">
              <Map className="h-3.5 w-3.5" />
              Notre trajectoire
            </span>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 mb-3">
              On ne fait pas que du marketing : on construit, on livre.
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto">
              Voici ce qui se prépare dans les prochains mois. Vous savez exactement où vous mettez les pieds.
            </p>
          </div>

          {/* 3 prochains jalons preview */}
          <div className="grid gap-5 md:grid-cols-3">
            {getUpcomingMilestones(3).map((m) => (
              <div
                key={m.id}
                className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition hover:shadow-md hover:-translate-y-0.5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-[0.1em] uppercase ${
                      m.status === "in_progress"
                        ? "bg-orange-100 text-orange-700"
                        : "bg-indigo-100 text-indigo-700"
                    }`}
                  >
                    {m.status === "in_progress" ? "⚡ En cours" : "✨ À venir"}
                  </span>
                  <span className="text-[11px] text-slate-400 font-medium">
                    {m.period}
                  </span>
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{m.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                  {m.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/roadmap"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 px-6 py-3 text-sm font-semibold text-white shadow-sm transition"
            >
              Voir la roadmap complète
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section className="py-20 bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-900 text-center relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(circle at 20% 0%, rgba(129,140,248,0.35) 0, transparent 55%), radial-gradient(circle at 80% 80%, rgba(249,115,22,0.25) 0, transparent 55%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4">
            Prêt à offrir un vrai moment à votre équipe ?
          </h2>
          <p className="text-sm sm:text-base text-indigo-100/90 mb-8 max-w-xl mx-auto">
            Achetez un pack, partagez un code. La partie démarre en quelques minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/catalogue"
              className="group inline-flex items-center justify-center px-8 py-3 rounded-full text-sm font-semibold text-white
                         bg-indigo-500 hover:bg-indigo-400 shadow-md shadow-indigo-500/30
                         ring-1 ring-white/10 transition-all duration-200"
            >
              Voir les jeux disponibles
              <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/offres"
              className="inline-flex items-center justify-center px-8 py-3 rounded-full text-sm font-semibold
                         text-indigo-100 border border-indigo-300/60 hover:border-indigo-200
                         hover:bg-indigo-900/40 ring-1 ring-white/10 transition-all duration-200"
            >
              Consulter les tarifs
            </Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-4 text-[12px] text-indigo-200/70">
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Accès instantané après paiement
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Facture automatique
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> 4 à 6 joueurs par session
            </span>
          </div>
        </div>
      </section>

      <TerminalModal open={terminalOpen} onClose={() => setTerminalOpen(false)} />

    </div>
  );
}

export default Home;